import type { Application, FederatedPointerEvent} from 'pixi.js';
import { Container, Graphics, Rectangle, Text } from 'pixi.js';
import { draw3DButton, drawBevelPanel, GUGI_SHADOW } from '../rendering/PanelUtils';
import { ACCENT, ACCENT_MED, ACCENT_DIM, ACCENT_DARK, PANEL_FILL as PANEL_BG } from '../rendering/LayoutConstants';
import type { SoundSystem } from './SoundSystem';
import type { GameStateManager } from '../states/GameStateManager';
import { ChangelogOverlay } from '../ui/ChangelogOverlay';
import { buildAchievementsOverlay } from '../rendering/AchievementsUI';

// ─── Settings Overlay ─────────────────────────────────────────
export class SettingsOverlay {
  private root = new Container();
  // Cleanup functions registered by sliders (remove stage listeners on close)
  private cleanups: (() => void)[] = [];
  private gameManager: GameStateManager | null;

  constructor(private app: Application, private sound: SoundSystem, gameManager?: GameStateManager) {
    this.gameManager = gameManager ?? null;
    this.build();
    app.stage.addChild(this.root);
  }

  private close() {
    for (const fn of this.cleanups) fn();
    this.cleanups = [];
    this.app.stage.removeChild(this.root);
    this.root.destroy({ children: true });
  }

  private build() {
    const { width: W, height: H } = this.app.screen;
    const panelW = 420;
    const padX   = 24;

    // ── Backdrop ──────────────────────────────────────────────
    const backdrop = new Graphics();
    backdrop.rect(0, 0, W, H);
    backdrop.fill({ color: 0x000000, alpha: 0.65 });
    backdrop.eventMode = 'static';
    backdrop.on('pointerdown', () => this.close());
    this.root.addChild(backdrop);

    // ── Panel ─────────────────────────────────────────────────
    const panel = new Container();
    this.root.addChild(panel);

    let y = 54; // content starts below title bar

    // Music section
    y = this.buildChannelSection(panel, 'MUSIC', y, panelW, padX,
      this.sound.musicVolume, this.sound.musicMuted,
      v => this.sound.setMusicVolume(v),
      m => this.sound.setMusicMuted(m),
    );

    // Divider
    const div = new Graphics();
    div.moveTo(padX, y + 10).lineTo(panelW - padX, y + 10);
    div.stroke({ color: ACCENT_DIM, width: 1, alpha: 0.25 });
    panel.addChild(div);
    y += 28;

    // SFX section
    y = this.buildChannelSection(panel, 'SOUND EFFECTS', y, panelW, padX,
      this.sound.sfxVolume, this.sound.sfxMuted,
      v => this.sound.setSfxVolume(v),
      m => this.sound.setSfxMuted(m),
    );

    // ── Save management section ─────────────────────────────
    if (this.gameManager) {
      const saveDiv = new Graphics();
      saveDiv.moveTo(padX, y + 10).lineTo(panelW - padX, y + 10);
      saveDiv.stroke({ color: ACCENT_DIM, width: 1, alpha: 0.25 });
      panel.addChild(saveDiv);
      y += 28;

      const saveLbl = new Text({ text: 'SAVE DATA', style: { fontFamily: 'Gugi', fontSize: 13, fill: 0x8899aa, dropShadow: GUGI_SHADOW } });
      saveLbl.position.set(padX, y);
      panel.addChild(saveLbl);
      y += 28;

      const btnW = 110, btnH = 30, btnGap = 12;
      const totalBtnW = btnW * 3 + btnGap * 2;
      const btnStartX = Math.round((panelW - totalBtnW) / 2);

      // Export Save button
      const exportBtn = this.buildTextButton('EXPORT SAVE', btnW, btnH, 0x1a1e28, 0x8899aa, () => {
        if (!this.gameManager) return;
        this.gameManager.save();
        const encoded = this.gameManager.exportSave();
        navigator.clipboard.writeText(encoded).then(() => {
          statusText.text = 'Save copied to clipboard!';
          statusText.style.fill = 0x44cc66;
        }).catch(() => {
          // Fallback: prompt with the text
          window.prompt('Copy this save data:', encoded);
          statusText.text = 'Save exported.';
          statusText.style.fill = 0x44cc66;
        });
      });
      exportBtn.position.set(btnStartX, y);
      panel.addChild(exportBtn);

      // Import Save button
      const importBtn = this.buildTextButton('IMPORT SAVE', btnW, btnH, 0x1a1e28, 0x8899aa, () => {
        if (!this.gameManager) return;
        const encoded = window.prompt('Paste save data:');
        if (!encoded) return;
        if (this.gameManager.importSave(encoded.trim())) {
          statusText.text = 'Save imported! Reloading...';
          statusText.style.fill = 0x44cc66;
          setTimeout(() => location.reload(), 600);
        } else {
          statusText.text = 'Invalid save data.';
          statusText.style.fill = ACCENT;
        }
      });
      importBtn.position.set(btnStartX + btnW + btnGap, y);
      panel.addChild(importBtn);

      // Reset Progress button
      const resetBtn = this.buildTextButton('RESET', btnW, btnH, ACCENT_DARK, ACCENT, () => {
        if (!this.gameManager) return;
        if (!confirm('Reset all progress? This cannot be undone.')) return;
        this.gameManager.resetProgress();
        statusText.text = 'Progress reset! Reloading...';
        statusText.style.fill = ACCENT;
        setTimeout(() => location.reload(), 600);
      });
      resetBtn.position.set(btnStartX + (btnW + btnGap) * 2, y);
      panel.addChild(resetBtn);

      y += btnH + 8;

      // Status text for feedback
      const statusText = new Text({ text: '', style: { fontFamily: 'Offside', fontSize: 11, fill: 0x667788 } });
      statusText.anchor.set(0.5, 0);
      statusText.position.set(panelW / 2, y);
      panel.addChild(statusText);
      y += 18;
    }

    // Divider before changelog
    const clDiv = new Graphics();
    clDiv.moveTo(padX, y + 10).lineTo(panelW - padX, y + 10);
    clDiv.stroke({ color: ACCENT_DIM, width: 1, alpha: 0.25 });
    panel.addChild(clDiv);
    y += 28;

    // Changelog + Achievements buttons (side by side).
    const btnW = 130;
    const btnH = 30;
    const btnGap = 12;
    const pairW = btnW * 2 + btnGap;
    const pairX = Math.round((panelW - pairW) / 2);

    const clBtn = this.buildTextButton('CHANGELOG', btnW, btnH, 0x1a1e28, 0x8899aa, () => {
      this.close();
      new ChangelogOverlay(this.app);
    });
    clBtn.position.set(pairX, y);
    panel.addChild(clBtn);

    const awardsBtn = this.buildTextButton('ACHIEVEMENTS', btnW, btnH, 0x251a2e, 0xddbbff, () => {
      if (!this.gameManager) return;
      this.close();
      const overlayRoot = new Container();
      this.app.stage.addChild(overlayRoot);
      const overlay = buildAchievementsOverlay(
        this.gameManager,
        this.app.screen.width, this.app.screen.height,
        () => {
          this.app.stage.removeChild(overlayRoot);
          overlayRoot.destroy({ children: true });
        },
      );
      overlayRoot.addChild(overlay.container);
    });
    awardsBtn.position.set(pairX + btnW + btnGap, y);
    panel.addChild(awardsBtn);
    y += 40;

    // Divider before credits
    const credDiv = new Graphics();
    credDiv.moveTo(padX, y + 14).lineTo(panelW - padX, y + 14);
    credDiv.stroke({ color: ACCENT_DIM, width: 1, alpha: 0.2 });
    panel.addChild(credDiv);
    y += 32;

    // Music credits
    const credLines = [
      'Music credits:',
      '"Bleeping Demo", "Space Fighter Loop" Kevin MacLeod (incompetech.com)',
      'Licensed under Creative Commons: By Attribution 4.0 License',
      'http://creativecommons.org/licenses/by/4.0/',
    ];
    for (const line of credLines) {
      const t = new Text({ text: line, style: {
        fontFamily: 'Offside', fontSize: 11, fill: 0x445566,
        wordWrap: true, wordWrapWidth: panelW - padX * 2,
      }});
      t.position.set(padX, y);
      panel.addChild(t);
      y += t.height + 2;
    }

    const panelH = y + 20; // bottom padding
    const panelX = Math.round((W - panelW) / 2);
    const panelY = Math.round((H - panelH) / 2);
    panel.position.set(panelX, panelY);

    // Background — inserted at index 0 so it renders behind all content
    const bg = new Graphics();
    drawBevelPanel(bg, 0, 0, panelW, panelH, { fill: PANEL_BG, border: ACCENT_DIM, radius: 8, accentEdge: ACCENT, rivets: true });
    // Block backdrop clicks from passing through the panel
    bg.eventMode = 'static';
    bg.on('pointerdown', e => e.stopPropagation());
    panel.addChildAt(bg, 0);

    // Title
    const title = new Text({ text: 'SETTINGS', style: { fontFamily: 'Gugi', fontSize: 22, fill: ACCENT, dropShadow: GUGI_SHADOW } });
    title.anchor.set(0.5, 0);
    title.position.set(panelW / 2, 14);
    panel.addChild(title);

    // Close button
    const closeBtn = this.buildCloseButton();
    closeBtn.position.set(panelW - 44, 11);
    panel.addChild(closeBtn);
  }

  // ── Close button (×) ────────────────────────────────────────
  private buildCloseButton(): Container {
    const c = new Container();
    const bg = new Graphics();
    bg.roundRect(0, 0, 32, 32, 4);
    bg.fill(0x1a1e28);
    bg.stroke({ color: ACCENT_DIM, width: 1 });
    c.addChild(bg);
    const label = new Text({ text: '×', style: { fontFamily: 'Offside', fontSize: 26, fill: 0x667788 } });
    label.anchor.set(0.5);
    label.position.set(16, 16);
    c.addChild(label);
    c.eventMode = 'static'; c.cursor = 'pointer';
    c.on('pointerdown', e => { e.stopPropagation(); this.close(); });
    return c;
  }

  // ── Channel section (label + slider + % + mute) ─────────────
  private buildChannelSection(
    panel: Container, label: string, y: number, panelW: number, padX: number,
    initialVolume: number, initialMuted: boolean,
    onVolume: (v: number) => void,
    onMute: (m: boolean) => void,
  ): number {
    // Section label
    const lbl = new Text({ text: label, style: { fontFamily: 'Gugi', fontSize: 13, fill: 0x8899aa, dropShadow: GUGI_SHADOW } });
    lbl.position.set(padX, y);
    panel.addChild(lbl);
    y += 26;

    // Layout (total inner = panelW - 2*padX = 372):
    //   slider: 220px | gap: 14 | pct: 46px | gap: 12 | mute: 80px  = 372 ✓
    const sliderW = 220;
    const muteW = 80, muteH = 30;
    const muteX  = panelW - padX - muteW;
    const pctX   = muteX - 12 - 46;
    const controlY = y;

    // Volume % label (updated by slider)
    const pctText = new Text({
      text: `${Math.round(initialVolume * 100)}%`,
      style: { fontFamily: 'Offside', fontSize: 14, fill: 0x667788 },
    });
    pctText.anchor.set(1, 0.5);
    pctText.position.set(pctX + 46, controlY + muteH / 2);
    panel.addChild(pctText);

    // Slider
    this.buildSlider(panel, padX, controlY + muteH / 2, sliderW, initialVolume, v => {
      pctText.text = `${Math.round(v * 100)}%`;
      onVolume(v);
    });

    // Mute toggle button
    let muted = initialMuted;
    const muteC = new Container();
    const muteBg = new Graphics();
    const muteLabel = new Text({
      text: '',
      style: { fontFamily: 'Offside', fontSize: 13, fill: 0xffffff },
    });
    muteLabel.anchor.set(0.5);
    muteLabel.position.set(muteW / 2, muteH / 2);
    muteC.addChild(muteBg);
    muteC.addChild(muteLabel);
    muteC.position.set(muteX, controlY);
    muteC.eventMode = 'static'; muteC.cursor = 'pointer';

    const redrawMute = () => {
      muteBg.clear();
      draw3DButton(muteBg, muteW, muteH, muted ? ACCENT_DARK : 0x1a1e28, { radius: 4, depth: 2 });
      muteLabel.text  = muted ? 'UNMUTE' : 'MUTE';
      muteLabel.style.fill = muted ? ACCENT : 0x8899aa;
    };
    redrawMute();

    muteC.on('pointerdown', e => {
      e.stopPropagation();
      muted = !muted;
      onMute(muted);
      redrawMute();
    });
    panel.addChild(muteC);

    y = controlY + muteH;
    return y;
  }

  // ── Text button (for save management) ────────────────────────
  private buildTextButton(
    text: string, w: number, h: number,
    bgColor: number, textColor: number,
    onClick: () => void,
  ): Container {
    const c = new Container();
    const bg = new Graphics();
    draw3DButton(bg, w, h, bgColor, { radius: 4, depth: 2 });
    c.addChild(bg);

    const label = new Text({ text, style: { fontFamily: 'Offside', fontSize: 12, fill: textColor } });
    label.anchor.set(0.5);
    label.position.set(w / 2, h / 2);
    c.addChild(label);

    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      onClick();
    });
    return c;
  }

  // ── Draggable slider ─────────────────────────────────────────
  private buildSlider(
    parent: Container, x: number, cy: number,
    trackW: number, initialValue: number,
    onChange: (v: number) => void,
  ) {
    const TRACK_H = 6;
    const HANDLE_R = 9;
    let value = Math.max(0, Math.min(1, initialValue));
    let dragging = false;

    const c = new Container();
    c.position.set(x, cy);
    parent.addChild(c);

    const trackBg = new Graphics();
    c.addChild(trackBg);

    const trackFill = new Graphics();
    c.addChild(trackFill);

    const handle = new Graphics();
    c.addChild(handle);

    const redraw = (v: number) => {
      value = Math.max(0, Math.min(1, v));
      const hx = value * trackW;

      trackBg.clear();
      trackBg.roundRect(0, -TRACK_H / 2, trackW, TRACK_H, 3);
      trackBg.fill(0x1a1e28);
      trackBg.stroke({ color: 0x2a3548, width: 1 });

      trackFill.clear();
      if (hx > 0) {
        trackFill.roundRect(0, -TRACK_H / 2, hx, TRACK_H, 3);
        trackFill.fill(ACCENT_MED);
      }

      handle.clear();
      handle.circle(hx, 0, HANDLE_R);
      handle.fill(0xdde4ee);
      handle.stroke({ color: ACCENT, width: 2 });
    };
    redraw(initialValue);

    // Interactive hit area: full track width + handle overflow, expanded
    // vertically to ~44px touch target per WCAG.
    const TOUCH_PAD_Y = 16;
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.hitArea = new Rectangle(
      -HANDLE_R, -HANDLE_R - TOUCH_PAD_Y,
      trackW + HANDLE_R * 2, HANDLE_R * 2 + TOUCH_PAD_Y * 2,
    );

    c.on('pointerdown', (e: FederatedPointerEvent) => {
      e.stopPropagation();
      dragging = true;
      const local = c.toLocal(e.global);
      redraw(local.x / trackW);
      onChange(value);
    });

    // Listen on backdrop's parent (the overlay root) so dragging outside the
    // slider still works — backdrop captures all pointer events over the screen.
    const onMove = (e: FederatedPointerEvent) => {
      if (!dragging) return;
      const local = c.toLocal(e.global);
      redraw(local.x / trackW);
      onChange(value);
    };
    const onUp = () => { dragging = false; };

    this.root.on('pointermove', onMove);
    this.root.on('pointerup', onUp);
    this.cleanups.push(() => {
      this.root.off('pointermove', onMove);
      this.root.off('pointerup', onUp);
    });
  }
}

// ─── Settings button (hamburger icon) ─────────────────────────
// Place this wherever a settings button is needed.
export function createSettingsButton(
  parent: Container,
  app: Application,
  sound: SoundSystem,
  x: number,
  y: number,
  gameManager?: GameStateManager,
): Container {
  const SIZE = 36;
  const btn = new Container();
  btn.position.set(x, y);

  const bg = new Graphics();
  bg.roundRect(0, 0, SIZE, SIZE, 5);
  bg.fill({ color: 0x0e1218, alpha: 0.92 });
  bg.stroke({ color: 0x334455, width: 1 });
  btn.addChild(bg);

  // Three horizontal lines (hamburger / settings icon)
  const icon = new Graphics();
  const lx = 8, lw = SIZE - 16;
  icon.moveTo(lx, 10).lineTo(lx + lw, 10);
  icon.moveTo(lx, 18).lineTo(lx + lw, 18);
  icon.moveTo(lx, 26).lineTo(lx + lw, 26);
  icon.stroke({ color: 0x8899aa, width: 2 });
  btn.addChild(icon);

  btn.eventMode = 'static'; btn.cursor = 'pointer';
  btn.on('pointerdown', () => {
    sound.resume(); // unlock AudioContext on first user interaction
    new SettingsOverlay(app, sound, gameManager);
  });

  parent.addChild(btn);
  return btn;
}
